import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Globe, Check, X, Loader2, ExternalLink, Users } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import Avatar from "./Avatar";

/**
 * Admin queue for camper-submitted website URLs for campsites that have
 * no `website` on file. Mirrors the PriceReportsCard pattern.
 */
export default function WebsiteReportsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // campsite_id currently being mutated
  const [overrides, setOverrides] = useState({}); // campsite_id -> string url

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/website-reports?status=open");
      setItems(data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async (campsiteId, url) => {
    const u = (url || "").trim();
    if (!u || u.length < 4 || !u.includes(".")) {
      toast.error("Enter a valid website URL");
      return;
    }
    setBusy(campsiteId);
    try {
      await api.post(`/admin/website-reports/${campsiteId}/apply`, { url: u });
      toast.success("Website added and reports resolved");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (campsiteId) => {
    if (!window.confirm("Dismiss all open website reports for this campsite?")) return;
    setBusy(campsiteId);
    try {
      const { data } = await api.post(`/admin/website-reports/${campsiteId}/dismiss`);
      toast.success(`Dismissed ${data.dismissed} report(s)`);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="website-reports-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Globe className="w-3 h-3" /> Website Reports
          </div>
          <div className="font-display text-xl mb-1">Crowdsourced missing websites</div>
          <p className="text-sm text-[#555] max-w-2xl">
            Campers submit official website URLs for sites that have none on file
            (~6,500 OSM-imported entries). Verify the link is genuine before
            applying — applying sets <code className="mono text-xs">website</code>,
            stamps <code className="mono text-xs">website_verified_at</code>, and
            recomputes the campsite's <code className="mono text-xs">book_rank</code>.
          </p>
        </div>
        <div className="flex items-center gap-2 mono text-xs text-[#888]">
          <Users className="w-3 h-3" />
          {items.length} campsite(s) with open reports
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="p-8 border hairline bg-[#fafafa] text-center text-sm text-[#888]" data-testid="website-reports-empty">
          No open website reports. Quiet on the front lines.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((it, idx) => {
            const overrideVal = overrides[it.campsite_id] ?? it.reports?.[0]?.url ?? "";
            return (
              <div key={it.campsite_id} className="p-4 border hairline bg-[#fafafa]" data-testid={`website-report-${idx}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div className="min-w-0">
                    <Link
                      to={`/campsites/${it.campsite_id}`}
                      className="font-display text-lg hover:underline inline-flex items-center gap-1 break-words"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {it.campsite_name || it.campsite_id}
                      <ExternalLink className="w-3 h-3 text-[#888] flex-shrink-0" />
                    </Link>
                    <div className="mono text-xs text-[#888]">
                      {it.campsite_region ? `${it.campsite_region} · ` : ""}
                      {it.report_count} report(s) · latest {new Date(it.latest_at).toLocaleString()}
                    </div>
                    {it.current_website && (
                      <div className="mono text-[11px] text-[#b91c1c] mt-1 break-all">
                        ⚠ already has website: {it.current_website}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="eyebrow mb-1.5">Submitted URLs</div>
                  <div className="flex flex-col gap-2">
                    {it.reports.map((r) => (
                      <div
                        key={r.report_id}
                        className="text-xs mono px-2 py-2 bg-white border hairline flex items-start gap-2"
                      >
                        <Avatar src={r.user_picture} name={r.user_name} size={20} ringed={false} />
                        <div className="min-w-0 flex-1">
                          <a
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#1f5fbf] hover:underline break-all inline-flex items-center gap-1"
                          >
                            {r.url}
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                          <div className="text-[#888] text-[10.5px] mt-0.5">
                            {r.user_name}{r.note ? ` — ${r.note}` : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOverrides({ ...overrides, [it.campsite_id]: r.url })}
                          className="text-[10px] mono uppercase tracking-wider px-2 py-1 border hairline hover:bg-[#fff5e6] hover:border-[#ff4500] flex-shrink-0"
                          title="Use this URL"
                        >
                          Use
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-xs flex-1 min-w-[200px]">
                    <span className="eyebrow whitespace-nowrap">Apply URL</span>
                    <input
                      type="url"
                      value={overrideVal}
                      onChange={(e) => setOverrides({ ...overrides, [it.campsite_id]: e.target.value })}
                      className="flex-1 border hairline px-2 py-1.5 text-sm focus:outline-none focus:border-[#ff4500] mono min-w-0"
                      data-testid={`website-input-${idx}`}
                    />
                  </label>
                  <button
                    onClick={() => apply(it.campsite_id, overrideVal)}
                    disabled={busy === it.campsite_id}
                    className="btn-primary !px-3 !py-2 text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`apply-website-${idx}`}
                  >
                    {busy === it.campsite_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Apply
                  </button>
                  <button
                    onClick={() => dismiss(it.campsite_id)}
                    disabled={busy === it.campsite_id}
                    className="btn-secondary !px-3 !py-2 text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`dismiss-website-${idx}`}
                  >
                    <X className="w-3 h-3" /> Dismiss all
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
